import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Plus, Pencil, Trash2, Tag, Search, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CAR_CATEGORIES = ['Autoturisme', 'SUV / Limuzine', 'XXL / +7 locuri'];

// Fallback pentru tipuri vechi fără câmpul category (aceeași regulă ca pe backend)
const categoryFromName = (name) => {
  const n = name.toLowerCase();
  if (n.includes('xxl') || n.includes('+7')) return 'XXL / +7 locuri';
  if (n.includes('suv') || n.includes('limuzin')) return 'SUV / Limuzine';
  return 'Autoturisme';
};

const SubscriptionTypes = () => {
  const { t } = useLanguage();
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [typeToDelete, setTypeToDelete] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: 0,
    duration_days: 30,
    wash_count: null,
    category: 'Autoturisme',
    is_recurring: false,
  });

  useEffect(() => {
    fetchTypes();
  }, []);

  const fetchTypes = async () => {
    try {
      const response = await axios.get(`${API}/subscription-types`);
      setTypes(response.data);
    } catch (error) {
      console.error('Error fetching subscription types:', error);
      toast.error('Error loading subscription types');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (type = null) => {
    if (type) {
      setEditingType(type);
      setFormData({
        name: type.name,
        description: type.description,
        price: type.price,
        duration_days: type.duration_days,
        wash_count: type.wash_count,
        category: type.category || categoryFromName(type.name),
        is_recurring: type.is_recurring ?? false,
      });
    } else {
      setEditingType(null);
      setFormData({
        name: '',
        description: '',
        price: 0,
        duration_days: 30,
        wash_count: null,
        category: 'Autoturisme',
        is_recurring: false,
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const dataToSend = {
      ...formData,
      wash_count: formData.wash_count === '' ? null : formData.wash_count
    };
    
    try {
      if (editingType) {
        await axios.put(`${API}/admin/subscription-types/${editingType.id}`, dataToSend);
        toast.success(t('subscription_type_updated'));
      } else {
        await axios.post(`${API}/admin/subscription-types`, dataToSend);
        toast.success(t('subscription_type_created'));
      }
      setDialogOpen(false);
      fetchTypes();
    } catch (error) {
      console.error('Error saving subscription type:', error);
      toast.error(error.response?.data?.detail || 'Error saving subscription type');
    }
  };

  const handleDelete = async () => {
    if (!typeToDelete) return;
    try {
      await axios.delete(`${API}/admin/subscription-types/${typeToDelete.id}`);
      toast.success(t('subscription_type_deleted'));
      setDeleteDialogOpen(false);
      setTypeToDelete(null);
      fetchTypes();
    } catch (error) {
      console.error('Error deleting subscription type:', error);
      toast.error(error.response?.data?.detail || 'Error deleting subscription type');
    }
  };

  const filteredTypes = types.filter(type =>
    type.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    type.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="subscription-types-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="subscription-types-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">{t('subscription_types')}</h1>
          <p className="text-muted-foreground mt-1">
            {types.length} {t('subscription_types').toLowerCase()}
          </p>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="add-subscription-type-btn">
          <Plus className="w-4 h-4 mr-2" />
          {t('add_subscription_type')}
        </Button>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder={t('search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-subscription-types"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {filteredTypes.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('subscription_type_name')}</TableHead>
                  <TableHead>{t('description')}</TableHead>
                  <TableHead>{t('category')}</TableHead>
                  <TableHead>{t('duration_days')}</TableHead>
                  <TableHead>{t('wash_count')}</TableHead>
                  <TableHead>Recurent</TableHead>
                  <TableHead>{t('price')}</TableHead>
                  <TableHead className="text-right">{t('actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTypes.map((type) => (
                  <TableRow key={type.id} data-testid={`subscription-type-row-${type.id}`}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <Tag className="w-4 h-4 text-primary" />
                        {type.name}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-xs truncate">{type.description}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{type.category || categoryFromName(type.name)}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{type.duration_days} {t('days')}</Badge>
                    </TableCell>
                    <TableCell>
                      {type.wash_count ? `${type.wash_count} ${t('washes')}` : t('unlimited')}
                    </TableCell>
                    <TableCell>
                      {type.is_recurring ? (
                        <Badge variant="secondary" className="gap-1">
                          <RefreshCw className="w-3 h-3" />
                          Recurent
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground text-sm">—</span>
                      )}
                    </TableCell>
                    <TableCell className="font-semibold">{type.price} {t('ron')}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenDialog(type)}
                          data-testid={`edit-subscription-type-${type.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setTypeToDelete(type);
                            setDeleteDialogOpen(true);
                          }}
                          data-testid={`delete-subscription-type-${type.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Tag className="w-12 h-12 mb-4 opacity-50" />
              <p>{t('no_subscription_types')}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]" data-testid="subscription-type-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingType ? t('edit_subscription_type') : t('add_subscription_type')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">{t('subscription_type_name')} *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="subscription-type-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">{t('description')} *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows={3}
                  data-testid="subscription-type-description-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="category">{t('valid_for_category')} *</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger id="category" data-testid="subscription-type-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CAR_CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  XXL / +7 locuri acoperă orice mașină; SUV / Limuzine acoperă și Autoturisme; Autoturisme acoperă doar Autoturisme.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="duration_days">{t('duration_days')} *</Label>
                  <Input
                    id="duration_days"
                    type="number"
                    min="1"
                    value={formData.duration_days}
                    onChange={(e) => setFormData({ ...formData, duration_days: parseInt(e.target.value) || 1 })}
                    required
                    data-testid="subscription-type-duration-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="wash_count">{t('wash_count')}</Label>
                  <Input
                    id="wash_count"
                    type="number"
                    min="1"
                    value={formData.wash_count || ''}
                    onChange={(e) => setFormData({ ...formData, wash_count: e.target.value ? parseInt(e.target.value) : null })}
                    placeholder={t('unlimited')}
                    data-testid="subscription-type-wash-count-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="price">{t('price')} ({t('ron')}) *</Label>
                <Input
                  id="price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                  required
                  data-testid="subscription-type-price-input"
                />
              </div>
              <div
                className="flex items-start gap-3 rounded-lg border border-border p-3 cursor-pointer select-none"
                onClick={() => setFormData({ ...formData, is_recurring: !formData.is_recurring })}
              >
                <div className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border ${formData.is_recurring ? 'bg-primary border-primary' : 'border-muted-foreground'}`}>
                  {formData.is_recurring && <span className="text-white text-xs font-bold leading-none">✓</span>}
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground flex items-center gap-1.5">
                    <RefreshCw className="w-3.5 h-3.5" />
                    Plată recurentă
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Abonamentul se reînnoiește automat. Utilizatorul va trebui să selecteze o mașină și să bifeze consimțământul pentru plată recurentă.
                  </p>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                {t('cancel')}
              </Button>
              <Button type="submit" data-testid="save-subscription-type-btn">
                {t('save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent data-testid="delete-subscription-type-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('confirm_delete')}</AlertDialogTitle>
            <AlertDialogDescription>
              {typeToDelete?.name}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} data-testid="confirm-delete-subscription-type">
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SubscriptionTypes;
